// 协议抽象, 对错误的处理不同, 及分页

import { AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios';
import { PlainObject } from './types';
import { TipError } from './Errors';
import { notifySuccess } from './EasyPopup';
import { toInteger, isString } from 'lodash-es';

type Response = {
  // 是否成功
  success: boolean;
  code: number;
  msg: string; // 错误消息

  // 返回的数据
  data: any;
};

// 分页参数
type PaginationParams = {
  // 分页大小
  pageSize: number;

  // 当前页
  currentPage: number;
};

// 分页查询结果
export type PaginationResult = {
  data: PlainObject[];
  totalCount: number;
};

// 接口差异抽象
export type ApiProtocol = {
  // 无错误返回 undefined
  parseResponse: (data: PlainObject) => Response;

  // 分页查询
  paginationRequest: (
    axios: AxiosInstance,
    url: string,
    searchParams: PlainObject | undefined,
    paginationParams: PaginationParams | undefined,
    config?: AxiosRequestConfig
  ) => Promise<PaginationResult>;
};

// y产品的协议
const erpy: ApiProtocol = {
  parseResponse: (data: PlainObject) => {
    return {
      success: data.code === 0 || !!data.success,
      code: data.errorCode || data.code,
      msg: data.msg,
      data: data.data
    };
  },

  paginationRequest: async (
    axios: AxiosInstance,
    url: string,
    searchParams: PlainObject | undefined,
    paginationParams: PaginationParams | undefined,
    config?: AxiosRequestConfig
  ) => {
    const resp = await axios.post(
      url,
      {
        ...(searchParams || {}),
        ...(paginationParams
          ? {
              currentPage: paginationParams.currentPage,
              pageSize: paginationParams.pageSize
            }
          : {})
      },
      config
    );

    const result = (assertRestResponse(resp) ||
      (paginationParams ? {} : [])) as any;

    // 分页模式下
    if (paginationParams) {
      const data = result.data || [];
      const totalCount = toInteger(result.totalCount);

      return {
        totalCount,
        data
      };
    }

    const data = result || [];

    // 非分页
    return {
      totalCount: data.length,
      data: data
    };
  }
};

// 企业版协议
const erp2: ApiProtocol = {
  parseResponse: (data: PlainObject) => {
    return {
      success: !data.status,
      code: data.status,
      msg: data.message,
      data: data.data
    };
  },

  paginationRequest: async (
    axios: AxiosInstance,
    url: string,
    searchParams: PlainObject | undefined,
    paginationParams: PaginationParams | undefined,
    config?: AxiosRequestConfig
  ) => {
    /**
     * 企业版当请求第二页的数据时 total 字段会返回0
     * 故判断总条数逻辑为 如若有total不为0 则使用total 为0时若有列表数据则使用-1 否则为0
     * 当为-1时 表格组件会使用上次total结果
     */
    const getTotalNum = (resp: Record<string, any>) => {
      const respData = resp?.data?.data || {};
      const row = respData.data || [];
      const total = respData.total;
      if (total) {
        return total;
      }
      return row.length ? -1 : 0;
    };

    const resp = await axios.post(url, searchParams || {}, {
      ...(config || {}),

      headers: paginationParams
        ? {
            'X-HC-Page': `${paginationParams.pageSize}-${
              paginationParams.currentPage - 1
            }-${paginationParams.currentPage == 1 ? 1 : 0}`
          }
        : {}
    });

    if (resp.status < 200 || resp.status >= 300) {
      throw new TipError(resp.statusText);
    }

    if (resp.data.status) {
      throw new TipError(resp.data.message || '未知服务器错误', {
        code: resp.data.status
      });
    }

    const total = getTotalNum(resp);

    if (resp.headers['content-type'] == 'application/x-resultset') {
      const rows = resultSetToList(resp.data);

      const totalCount = paginationParams ? toInteger(total) : rows.length;

      return {
        totalCount,
        data: rows
      };
    }

    const result = resp.data;
    const data = result.data || [];
    if (paginationParams) {
      const totalCount = toInteger(total);

      return {
        totalCount,
        data
      };
    }

    return {
      totalCount: data.length,
      data: data
    };
  }
};

const apiProtocolMap = {
  erpy: erpy,
  erp2: erp2
};

// 默认协议为erpy
let apiProtocol: ApiProtocol = erpy;
export function getApiProtocol() {
  return apiProtocol;
}

export function setApiProtocol(protocol: ApiProtocol | 'erp2' | 'erpy') {
  if (isString(protocol)) {
    apiProtocol = apiProtocolMap[protocol];
  } else {
    apiProtocol = protocol;
  }
}

/////////////////////////////////////////////////
// application/x-resultset 转换
function resultSetToList(resultset: any) {
  // 判断列里是否包含停用字段
  const columns = resultset.data.columns;
  const rows = resultset.data.data as any[][];
  const rowCount = rows.length;
  const columnCount = columns.length;

  const list = [] as PlainObject[];
  list.length = rowCount;

  for (let j = 0; j < rowCount; j++) {
    const row = rows[j];

    const o = {} as PlainObject;
    for (let i = 0; i < columnCount; i++) {
      o[columns[i]] = row[i];
    }

    list[j] = o;
  }

  return list;
}

export function assertRestResponse<T, D>(
  resp: AxiosResponse<{ data?: T }, D>,
  successMsg?: string
): T {
  if (resp.status < 200 || resp.status >= 300) {
    throw new TipError(resp.statusText);
  }

  const result = apiProtocol.parseResponse(resp.data);

  if (!result.success) {
    throw new TipError(result.msg || '未知服务器错误', { code: result.code });
  }

  if (successMsg !== undefined) {
    notifySuccess(successMsg || result.msg || '操作成功');
  }

  if (resp.headers['content-type'] == 'application/x-resultset') {
    return resultSetToList(resp.data) as T;
  }

  return result.data;
}
